import { useState, useMemo, useEffect, useCallback } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useConfiguration } from "../../../components/ConfigurationContext";
import { useBranch } from "../../../components/BranchContext";
import { accountingService } from "../../../../../services/accounting";
import {
  ILedgerSummaryRequest,
  ILedgerEntriesRequest,
  ILedgerSummary,
  ILedgerEntry,
  IJournalDetailsRequest,
  IJournalDetailsData,
  IListCoaRequest,
  IChartOfAccount,
} from "../../../accounting.interface";

// Updated interfaces to match API responses
export interface LedgerFilters {
  dateFrom?: string;
  dateTo?: string;
  selectedBranch?: string;
  sourceModule?:
    | "POS"
    | "INVENTORY"
    | "HR"
    | "FINANCE"
    | "MANUAL_JOURNAL"
    | "SYSTEM_SETUP"
    | "";
  journalEntryId?: string;
  referenceSearch?: string;
  voucherType?: string;
  onlyPostedJournals?: boolean;
  includeOpeningBalance?: boolean;
}

export interface AccountSummary {
  openingBalance: number;
  closingBalance: number;
  totalDebits: number;
  totalCredits: number;
}

export interface LedgerTableEntry extends ILedgerEntry {
  _id: string;
}

export const useLedgerData = () => {
  const { ledgerSettings } = useConfiguration();
  const { selectedBranch } = useBranch();

  // Core state
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [selectedAccount, setSelectedAccount] =
    useState<IChartOfAccount | null>(null);
  const [postingAccounts, setPostingAccounts] = useState<IChartOfAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter state - initialized with configuration settings
  const [filters, setFilters] = useState<LedgerFilters>({
    dateFrom: moment().subtract(1, "year").format("YYYY-MM-DD"),
    dateTo: moment().format("YYYY-MM-DD"),
    selectedBranch: "organization",
    sourceModule: "",
    journalEntryId: "",
    referenceSearch: "",
    voucherType: "",
    onlyPostedJournals: !ledgerSettings?.include_draft_journals_in_ledger,
    includeOpeningBalance: ledgerSettings?.enable_opening_balances,
  });

  // Data state
  const [accountSummary, setAccountSummary] = useState<AccountSummary>({
    openingBalance: 0,
    closingBalance: 0,
    totalDebits: 0,
    totalCredits: 0,
  });
  const [ledgerEntries, setLedgerEntries] = useState<ILedgerEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Modal state
  const [selectedJournalEntry, setSelectedJournalEntry] =
    useState<IJournalDetailsData | null>(null);
  const [isJournalDialogOpen, setIsJournalDialogOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch posting accounts from COA API
  const fetchPostingAccounts = useCallback(async () => {
    try {
      const requestData: IListCoaRequest = {
        pageIndex: 0,
        pageSize: 1000, // Get all accounts
        sortField: "account_code",
        sortOrder: "asc",
        selected_branch_id:
          filters.selectedBranch === "organization"
            ? undefined
            : filters.selectedBranch,
      };

      const response = await accountingService.listCoa(requestData);

      // Convert API accounts to UI format and filter for posting accounts
      const accounts = response.data.map(
        (account): IChartOfAccount => ({
          id: account.id,
          accountCode: account.account_code,
          accountName: account.account_name,
          accountType: account.account_type,
          currency: account.currency,
          openingBalance: account.opening_balance,
          currentBalance: account.current_balance,
          parentAccountId: account.parent_account_id || undefined,
          isParent: account.is_parent,
          allowDirectPosting: account.allow_direct_posting,
          level: account.level,
          isActive: account.is_active,
          createdAt: new Date(account.created_at).toISOString(),
          updatedAt: new Date(account.updated_at).toISOString(),
        })
      );

      let filteredAccounts = accounts;

      // Apply configuration filters
      if (ledgerSettings?.show_only_postable_accounts) {
        filteredAccounts = filteredAccounts.filter(
          (account) => account.allowDirectPosting
        );
      }

      if (ledgerSettings?.hide_zero_balance_accounts) {
        filteredAccounts = filteredAccounts.filter(
          (account) =>
            (account.currentBalance || 0) !== 0 ||
            (account.openingBalance || 0) !== 0
        );
      }

      if (!ledgerSettings?.show_archived_accounts) {
        filteredAccounts = filteredAccounts.filter(
          (account) => account.isActive !== false
        );
      }

      setPostingAccounts(filteredAccounts);
    } catch (error: any) {
      console.error("Failed to fetch accounts:", error);
      toast.error("Failed to fetch accounts for ledger");
      setPostingAccounts([]);
    }
  }, [filters.selectedBranch, ledgerSettings]);

  // Fetch ledger summary for selected account
  const fetchLedgerSummary = useCallback(
    async (accountId: string) => {
      if (!accountId) return;

      try {
        const requestData: ILedgerSummaryRequest = {
          account_id: accountId,
          start_date: filters.dateFrom
            ? moment(filters.dateFrom).valueOf()
            : undefined,
          end_date: moment().valueOf(),
          selected_branch_id:
            filters.selectedBranch === "organization"
              ? undefined
              : filters.selectedBranch,
        };

        const response = await accountingService.getLedgerSummary(requestData);
        const summary = response.data;

        setAccountSummary({
          openingBalance: summary.opening_balance,
          closingBalance: summary.closing_balance,
          totalDebits: summary.total_debits,
          totalCredits: summary.total_credits,
        });
      } catch (error: any) {
        console.error("Failed to fetch ledger summary:", error);
        toast.error("Failed to fetch ledger summary");
      }
    },
    [filters.dateFrom, filters.dateTo, filters.selectedBranch]
  );

  // Fetch ledger entries for selected account
  const fetchLedgerEntries = useCallback(
    async (accountId: string) => {
      if (!accountId) {
        setLedgerEntries([]);
        setTotalCount(0);
        return;
      }

      setIsLoading(true);
      try {
        const requestData: ILedgerEntriesRequest = {
          account_id: accountId,
          start_date: filters.dateFrom
            ? moment(filters.dateFrom).valueOf()
            : undefined,
          end_date: moment().valueOf(),
          selected_branch_id:
            filters.selectedBranch === "organization"
              ? undefined
              : filters.selectedBranch,
          source_module: filters.sourceModule || undefined,
          search_query: filters.referenceSearch,
          journal_id_search: filters.journalEntryId,
          page_index: page,
          page_size: rowsPerPage,
          sort_field: "created_at",
          sort_order: "asc",
        };

        const response = await accountingService.getLedgerEntries(requestData);
        const data = response.data;

        setLedgerEntries(data.entries);
        setTotalCount(data.total_count);
      } catch (error: any) {
        console.error("Failed to fetch ledger entries:", error);
        toast.error("Failed to fetch ledger entries");
        setLedgerEntries([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [filters, page, rowsPerPage]
  );

  // Fetch journal details when user clicks on an entry
  const fetchJournalDetails = useCallback(async (journalEntryId: string) => {
    try {
      const requestData: IJournalDetailsRequest = {
        journal_entry_id: journalEntryId,
      };
      const response = await accountingService.getJournalDetails(requestData);
      setSelectedJournalEntry(response.data);
    } catch (error: any) {
      console.error("Failed to fetch journal details:", error);
      toast.error("Failed to fetch journal details");
    }
  }, []);

  // Effect to fetch accounts on mount and when branch changes
  useEffect(() => {
    fetchPostingAccounts();
  }, [fetchPostingAccounts]);

  // Update selected account when ID changes
  useEffect(() => {
    if (selectedAccountId) {
      const account = postingAccounts.find(
        (acc) => acc.id === selectedAccountId
      );
      setSelectedAccount(account || null);
    } else {
      setSelectedAccount(null);
    }
  }, [selectedAccountId, postingAccounts]);

  // Effect to fetch data when selected account or filters change
  useEffect(() => {
    if (selectedAccountId) {
      fetchLedgerSummary(selectedAccountId);
      fetchLedgerEntries(selectedAccountId);
    }
  }, [selectedAccountId, fetchLedgerSummary, fetchLedgerEntries]);

  // Prepare data for table
  const tableData = useMemo(() => {
    return ledgerEntries.map((entry, index) => ({
      ...entry,
      _id: `${entry.journal_entry_id}-${index}`,
    }));
  }, [ledgerEntries]);

  // Handlers
  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    setPage(0); // Reset pagination when account changes
  };

  const handleFiltersChange = (newFilters: Partial<LedgerFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0); // Reset pagination when filters change
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: moment().subtract(1, "month").format("YYYY-MM-DD"),
      dateTo: moment().format("YYYY-MM-DD"),
      selectedBranch: "organization",
      sourceModule: "",
      journalEntryId: "",
      referenceSearch: "",
      voucherType: "",
      onlyPostedJournals: !ledgerSettings?.include_draft_journals_in_ledger,
      includeOpeningBalance: ledgerSettings?.enable_opening_balances,
    });
    setPage(0);
  };

  const handleJournalEntryClick = async (entry: LedgerTableEntry) => {
    await fetchJournalDetails(entry.journal_entry_id);
    setIsJournalDialogOpen(true);
  };

  const handleCloseJournalDialog = () => {
    setIsJournalDialogOpen(false);
    setSelectedJournalEntry(null);
  };

  const handleCopyJournalId = (journalId: string) => {
    navigator.clipboard.writeText(journalId);
    toast.success("Journal ID copied to clipboard");
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0); // Reset to first page
  };

  const refreshData = useCallback(async () => {
    if (selectedAccountId) {
      await Promise.all([
        fetchLedgerSummary(selectedAccountId),
        fetchLedgerEntries(selectedAccountId),
      ]);
    }
    await fetchPostingAccounts();
  }, [
    selectedAccountId,
    fetchLedgerSummary,
    fetchLedgerEntries,
    fetchPostingAccounts,
  ]);

  return {
    // State
    selectedAccountId,
    selectedAccount,
    filters,
    page,
    rowsPerPage,
    selectedJournalEntry,
    isJournalDialogOpen,
    isLoading,
    totalCount,

    // Computed data
    postingAccounts,
    accountSummary,
    tableData,

    // Handlers
    handleAccountChange,
    handleFiltersChange,
    handleResetFilters,
    handleJournalEntryClick,
    handleCloseJournalDialog,
    handleCopyJournalId,
    handlePageChange,
    handlePageSizeChange,
    refreshData,
  };
};
